import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GradeBadge, AccessBadge, VerifiedTag } from "@/components/n360/Badges";
import { GRADING } from "@/lib/content";

export default function Directory() {
  const { meta } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ q: "", pillar: "", sub_sector: "", grade: "", access_level: "" });
  const [data, setData] = useState({ results: [], count: 0 });
  const [loading, setLoading] = useState(true);

  const pillars = meta?.pillars || [];
  const subSectors = pillars.find((p) => p.id === filters.pillar)?.subSectors || [];

  const load = async (f) => {
    setLoading(true);
    try {
      const { data } = await api.get("/directory", { params: f });
      setData(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(filters); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(() => load(filters), 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [filters]);

  const set = (k, v) => setFilters((prev) => ({ ...prev, [k]: v, ...(k === "pillar" ? { sub_sector: "" } : {}) }));
  const pillarName = (id) => pillars.find((p) => p.id === id)?.name || id;

  const chip = "px-3 py-2 rounded-lg bg-[#F6EEDC] border border-[#F1551D]/20 text-[#154859] text-sm focus:outline-none focus:border-[#F1551D]/60";

  return (
    <div className="rise">
      <div className="mb-6">
        <div className="font-mono-i text-[11px] tracking-[0.3em] text-[#F1551D]/90 uppercase mb-2">Verified Principals</div>
        <h1 className="font-display text-3xl sm:text-4xl text-[#0D3C4C]">Member Directory</h1>
        <p className="text-[#5f7d86] mt-1">{data.count} principals matched your clearance query.</p>
      </div>

      <div className="n360-card p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <Icons.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8598a0]" />
            <input data-testid="member-directory-search-input" value={filters.q} onChange={(e) => set("q", e.target.value)} placeholder="Search name, headline, organization, country…"
              className="w-full pl-10 pr-4 py-2 rounded-lg bg-[#F6EEDC] border border-[#F1551D]/20 text-[#0D3C4C] placeholder:text-[#aab9bd] focus:outline-none focus:border-[#F1551D]/60" />
          </div>
          <select data-testid="filter-pillar" value={filters.pillar} onChange={(e) => set("pillar", e.target.value)} className={chip}>
            <option value="" className="bg-[#F6EEDC]">All Pillars</option>
            {pillars.map((p) => <option key={p.id} value={p.id} className="bg-[#F6EEDC]">{p.name}</option>)}
          </select>
          <select data-testid="filter-subsector" value={filters.sub_sector} onChange={(e) => set("sub_sector", e.target.value)} className={chip} disabled={!subSectors.length}>
            <option value="" className="bg-[#F6EEDC]">All Sub-Sectors</option>
            {subSectors.map((s) => <option key={s} value={s} className="bg-[#F6EEDC]">{s}</option>)}
          </select>
          <select data-testid="filter-grade" value={filters.grade} onChange={(e) => set("grade", e.target.value)} className={chip}>
            <option value="" className="bg-[#F6EEDC]">All Tiers</option>
            {GRADING.map((g) => <option key={g.tier} value={g.tier} className="bg-[#F6EEDC]">{g.tier}</option>)}
          </select>
          <select data-testid="filter-access" value={filters.access_level} onChange={(e) => set("access_level", e.target.value)} className={chip}>
            <option value="" className="bg-[#F6EEDC]">All Access</option>
            {(meta?.access_levels || []).map((a) => <option key={a} value={a} className="bg-[#F6EEDC]">{a}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-[#8598a0]">Scanning the network…</div>
      ) : data.results.length === 0 ? (
        <div className="text-center py-20 text-[#8598a0]">No principals match your query.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.results.map((m) => (
            <button key={m.id} onClick={() => navigate(`/app/member/${m.id}`)} data-testid={`member-card-${m.id}`} className="n360-card p-5 text-left group">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl overflow-hidden bg-[#F6EEDC] border border-[#F1551D]/20 shrink-0">
                  {m.avatar_url ? <img src={m.avatar_url} alt={m.name} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-[#F1551D] font-display">{m.name?.[0]}</div>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-[#0D3C4C] font-semibold truncate group-hover:text-[#B84214] transition-colors">{m.name}</h3>
                  </div>
                  <p className="text-xs text-[#5f7d86] line-clamp-2 mt-0.5">{m.headline}</p>
                  <div className="text-[11px] text-[#8598a0] mt-1">{m.organization} · {m.country}</div>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div className="flex gap-1.5"><GradeBadge grade={m.grade} /><AccessBadge level={m.access_level} /></div>
                <VerifiedTag verified={m.verified} />
              </div>
              <div className="mt-3 text-[11px] text-[#8598a0]">{pillarName(m.pillar)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
